(() => {
  const DESKTOP_BREAKPOINT = 990;
  const CAROUSEL_SELECTOR =
    ".product__media-wrapper slider-component";

  class ProductMediaCarousel {
    constructor(sliderComponent) {
      this.sliderComponent = sliderComponent;

      this.slider = sliderComponent.querySelector(
        ".product__media-list"
      );

      this.slides = Array.from(
        sliderComponent.querySelectorAll(
          ".product__media-item"
        )
      );

      this.prevButton = sliderComponent.querySelector(
        ".slider-button--prev"
      );

      this.nextButton = sliderComponent.querySelector(
        ".slider-button--next"
      );

      this.currentCounter =
        sliderComponent.querySelector(
          ".slider-counter--current"
        );

      this.totalCounter =
        sliderComponent.querySelector(
          ".slider-counter--total"
        );

      this.currentIndex = 0;
      this.animationFrame = null;

      this.handlePrevClick =
        this.handlePrevClick.bind(this);

      this.handleNextClick =
        this.handleNextClick.bind(this);

      this.handleScroll =
        this.handleScroll.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      if (
        !this.slider ||
        !this.slides.length ||
        !this.prevButton ||
        !this.nextButton
      ) {
        return;
      }

      this.init();
    }

    init() {
      if (
        this.sliderComponent.dataset
          .productCarouselInitialized === "true"
      ) {
        return;
      }

      this.sliderComponent.dataset
        .productCarouselInitialized = "true";

      this.prevButton.addEventListener(
        "click",
        this.handlePrevClick
      );

      this.nextButton.addEventListener(
        "click",
        this.handleNextClick
      );

      this.slider.addEventListener(
        "scroll",
        this.handleScroll,
        { passive: true }
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        { passive: true }
      );

      this.updateState();
    }

    getVisibleSlideCount() {
      return window.innerWidth >= DESKTOP_BREAKPOINT
        ? 2
        : 1;
    }

    getMaxIndex() {
      return Math.max(
        this.slides.length -
          this.getVisibleSlideCount(),
        0
      );
    }

    getSlideStep() {
      const firstSlide = this.slides[0];

      if (!firstSlide) {
        return this.slider.clientWidth;
      }

      const sliderStyle = window.getComputedStyle(
        this.slider
      );

      const slideStyle = window.getComputedStyle(
        firstSlide
      );

      const gap =
        parseFloat(sliderStyle.columnGap) ||
        parseFloat(sliderStyle.gap) ||
        0;

      const marginLeft =
        parseFloat(slideStyle.marginLeft) || 0;

      const marginRight =
        parseFloat(slideStyle.marginRight) || 0;

      return (
        firstSlide.getBoundingClientRect().width +
        gap +
        marginLeft +
        marginRight
      );
    }

    handlePrevClick(event) {
      event.preventDefault();
      event.stopPropagation();

      this.goToSlide(this.currentIndex - 1);
    }

    handleNextClick(event) {
      event.preventDefault();
      event.stopPropagation();

      this.goToSlide(this.currentIndex + 1);
    }

    goToSlide(index) {
      this.currentIndex = Math.min(
        Math.max(index, 0),
        this.getMaxIndex()
      );

      this.slider.scrollTo({
        left:
          this.currentIndex * this.getSlideStep(),
        behavior: "smooth",
      });

      this.updateControls();
    }

    handleScroll() {
      if (this.animationFrame) {
        cancelAnimationFrame(
          this.animationFrame
        );
      }

      this.animationFrame =
        requestAnimationFrame(() => {
          const slideStep =
            this.getSlideStep();

          if (!slideStep) return;

          this.currentIndex = Math.round(
            this.slider.scrollLeft / slideStep
          );

          this.currentIndex = Math.min(
            Math.max(this.currentIndex, 0),
            this.getMaxIndex()
          );

          this.updateControls();
        });
    }

    handleResize() {
      this.currentIndex = Math.min(
        this.currentIndex,
        this.getMaxIndex()
      );

      this.slider.scrollTo({
        left:
          this.currentIndex * this.getSlideStep(),
        behavior: "auto",
      });

      this.updateControls();
    }

    updateState() {
      if (this.totalCounter) {
        this.totalCounter.textContent =
          String(this.slides.length);
      }

      this.updateControls();
    }

    updateControls() {
      const maxIndex = this.getMaxIndex();

      this.prevButton.disabled =
        this.currentIndex <= 0;

      this.nextButton.disabled =
        this.currentIndex >= maxIndex;

      if (this.currentCounter) {
        this.currentCounter.textContent =
          String(this.currentIndex + 1);
      }

      this.slides.forEach(
        (slide, index) => {
          slide.classList.toggle(
            "is-active",
            index === this.currentIndex
          );
        }
      );
    }

    destroy() {
      this.prevButton.removeEventListener(
        "click",
        this.handlePrevClick
      );

      this.nextButton.removeEventListener(
        "click",
        this.handleNextClick
      );

      this.slider.removeEventListener(
        "scroll",
        this.handleScroll
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      if (this.animationFrame) {
        cancelAnimationFrame(
          this.animationFrame
        );
      }

      this.sliderComponent.removeAttribute(
        "data-product-carousel-initialized"
      );
    }
  }

  const instances = new Map();

  function getComponents(root = document) {
    if (
      root instanceof Element &&
      root.matches(CAROUSEL_SELECTOR)
    ) {
      return [root];
    }

    return Array.from(
      root.querySelectorAll(
        CAROUSEL_SELECTOR
      )
    );
  }

  function initializeCarousels(
    root = document
  ) {
    getComponents(root).forEach(
      (component) => {
        if (instances.has(component)) {
          return;
        }

        const instance =
          new ProductMediaCarousel(
            component
          );

        if (instance.slider) {
          instances.set(
            component,
            instance
          );
        }
      }
    );
  }

  function destroyCarousels(
    root = document
  ) {
    getComponents(root).forEach(
      (component) => {
        const instance =
          instances.get(component);

        if (!instance) return;

        instance.destroy();
        instances.delete(component);
      }
    );
  }

  function initialize() {
    initializeCarousels();
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeCarousels(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroyCarousels(event.target);
    }
  );
})();